import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, RefreshCw, Eye, EyeOff, Trash2, BadgeCheck, Send, Sparkles } from 'lucide-react';

/**
 * iter 145 (Section 8) - Admin Community Ambassadors page.
 * iter 160 - Fix 5: AI posting controls (topic chips, cadence, on/off, Post now).
 *
 * Lets Francisco + Rodrigo create / edit / delete the 10 seeded
 * ambassador accounts that post on community boards. The "Mundalo
 * Community" badge is rendered here in admin only - never on the
 * member-facing portal.
 */
const API = process.env.REACT_APP_BACKEND_URL || '';

// iter 160 - Canonical topic vocabulary. Must match backend
// `AMBASSADOR_TOPICS` in routes/admin_ambassadors.py.
const TOPIC_VOCAB = [
  'visa', 'housing', 'schools', 'banking', 'healthcare', 'pets',
  'moving-logistics', 'neighborhoods', 'safety', 'remote-work', 'social-life',
];
const FREQ_OPTIONS = [2, 3, 4];

const authHeaders = () => {
  const t = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
};

const initialFormState = {
  first_name: '',
  last_name: '',
  email: '',
  bio: '',
  destination_city: '',
  home_state: '',
  age: '',
  profile_photo: '',
};

export default function AdminAmbassadors() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [busy, setBusy] = useState(false);

  // iter 162 - Post-now debounce state. Each ambassador's last-click
  // timestamp + transient success-flash banner. Combined with the
  // confirmation modal below, kills double-tap duplicate posts.
  const [postNowCooldowns, setPostNowCooldowns] = useState({});
  const [postNowFlash, setPostNowFlash] = useState({});  // {amb_id: 'success|error|preview'}
  const [postNowFlashMsg, setPostNowFlashMsg] = useState({});  // {amb_id: 'text'}
  const [confirmPostNow, setConfirmPostNow] = useState(null);  // ambassador object

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/ambassadors`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (r.status === 401) { navigate('/admin/login'); return; }
      const d = await r.json();
      setList(d.ambassadors || []);
    } catch {
      setError('Could not load ambassadors.');
      setList([]);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      first_name: a.first_name || '',
      last_name: a.last_name || '',
      email: a.email || '',
      bio: a.bio || '',
      destination_city: a.destination_city || '',
      home_state: a.home_state || '',
      age: a.age || '',
      profile_photo: a.profile_photo || '',
    });
  };

  const cancelEdit = () => { setEditing(null); setForm(initialFormState); };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        first_name: form.first_name,
        last_name:  form.last_name,
        bio:        form.bio,
        destination_city: form.destination_city,
        home_state: form.home_state,
        age:        form.age ? parseInt(form.age, 10) : null,
        profile_photo: form.profile_photo,
      };
      let r;
      if (editing && editing !== 'new') {
        r = await fetch(`${API}/api/admin/ambassadors/${editing}`, {
          method: 'PATCH', credentials: 'include',
          headers: authHeaders(), body: JSON.stringify(payload),
        });
      } else {
        r = await fetch(`${API}/api/admin/ambassadors`, {
          method: 'POST', credentials: 'include',
          headers: authHeaders(),
          body: JSON.stringify({ ...payload, email: form.email, visible: true }),
        });
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      await load();
      cancelEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (a) => {
    await fetch(`${API}/api/admin/ambassadors/${a.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ visible: !a.visible }),
    });
    load();
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.first_name} ${a.last_name}? This cannot be undone.`)) return;
    await fetch(`${API}/api/admin/ambassadors/${a.id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    load();
  };

  // iter 160 - patch helper used by all AI-control toggles. Optimistically
  // updates the local list so the UI reacts instantly; reloads from server
  // on failure to undo any optimistic mutation.
  const patchAmbassador = async (id, body) => {
    setList((prev) => prev?.map((a) => (a.id === id ? { ...a, ...body } : a)));
    try {
      const r = await fetch(`${API}/api/admin/ambassadors/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Patch failed');
    } catch {
      load();
    }
  };

  const toggleTopic = (a, topic) => {
    const current = a.topic_focus || [];
    let next;
    if (current.includes(topic)) {
      next = current.filter((t) => t !== topic);
    } else {
      if (current.length >= 4) return;  // hard cap, matches backend
      next = [...current, topic];
    }
    patchAmbassador(a.id, { topic_focus: next });
  };

  const setFrequency = (a, days) => {
    patchAmbassador(a.id, { posting_frequency_days: days });
  };

  const toggleAiPosting = (a) => {
    patchAmbassador(a.id, { ai_posting_enabled: !a.ai_posting_enabled });
  };

  // iter 162 - opens confirmation modal instead of posting immediately.
  const requestPostNow = (a) => {
    const lastClick = postNowCooldowns[a.id];
    if (lastClick && (Date.now() - lastClick) < 60_000) {
      return;  // still cooling down, ignore
    }
    setConfirmPostNow(a);
  };

  const cancelPostNow = () => setConfirmPostNow(null);

  const confirmAndPostNow = async () => {
    const a = confirmPostNow;
    if (!a) return;
    setConfirmPostNow(null);
    // Start 60s cooldown immediately on click so a double-tap on the
    // confirm button can't fire twice either.
    setPostNowCooldowns((prev) => ({ ...prev, [a.id]: Date.now() }));
    setPostNowFlash((prev) => ({ ...prev, [a.id]: 'pending' }));
    setPostNowFlashMsg((prev) => ({ ...prev, [a.id]: 'Generating post…' }));
    try {
      const r = await fetch(`${API}/api/admin/ambassadors/${a.id}/post-now`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = d.detail || `Post failed (HTTP ${r.status})`;
        setPostNowFlash((prev) => ({ ...prev, [a.id]: 'error' }));
        setPostNowFlashMsg((prev) => ({ ...prev, [a.id]: msg }));
        return;
      }
      setPostNowFlash((prev) => ({ ...prev, [a.id]: 'success' }));
      setPostNowFlashMsg((prev) => ({
        ...prev,
        [a.id]: `Posted to ${d.channel}: "${(d.content || '').slice(0, 90)}…"`,
      }));
      load();
    } catch (e) {
      setPostNowFlash((prev) => ({ ...prev, [a.id]: 'error' }));
      setPostNowFlashMsg((prev) => ({ ...prev, [a.id]: e.message || 'Network error' }));
    }
  };

  // Tick once per second so the per-button countdown re-renders.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const seed = async () => {
    if (busy) return;
    if (!window.confirm('Seed 10 curated ambassador profiles? Existing ones with matching emails will be updated.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/ambassadors/seed`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      const d = await r.json();
      alert(`Created ${d.created} · Updated ${d.updated} · Total ${d.total}`);
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF] p-6 md:p-10" data-testid="admin-ambassadors-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-[#1A5C6E]" />
            <h1 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E]">Community Ambassadors</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={seed}
              disabled={busy}
              data-testid="ambassador-seed-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1F2E] text-white text-sm hover:bg-[#163448] disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" /> Seed 10
            </button>
            <button
              onClick={() => { setEditing('new'); setForm(initialFormState); }}
              data-testid="ambassador-new-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8A830] text-[#0A1F2E] text-sm font-semibold hover:brightness-95"
            >
              <Plus className="w-4 h-4" /> New
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

        {editing && (
          <section className="bg-white border border-[#EFE6CF] rounded-2xl p-6 mb-6" data-testid="ambassador-form">
            <h2 className="font-display italic text-xl mb-4 text-[#0A1F2E]">
              {editing === 'new' ? 'New ambassador' : 'Edit ambassador'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <input className="border rounded p-2" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <input className="border rounded p-2" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <input
                className="border rounded p-2 md:col-span-2"
                placeholder="email@mundalo-community.com"
                value={form.email}
                disabled={editing !== 'new'}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input className="border rounded p-2" placeholder="Destination city" value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })} />
              <input className="border rounded p-2" placeholder="Home state" value={form.home_state} onChange={(e) => setForm({ ...form, home_state: e.target.value })} />
              <input className="border rounded p-2" placeholder="Age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
              <input className="border rounded p-2" placeholder="Profile photo URL" value={form.profile_photo} onChange={(e) => setForm({ ...form, profile_photo: e.target.value })} />
              <textarea className="border rounded p-2 md:col-span-2 min-h-[100px]" placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} disabled={busy} data-testid="ambassador-save-btn" className="px-4 py-2 rounded-full bg-[#E8A830] text-[#0A1F2E] font-semibold disabled:opacity-50">Save</button>
              <button onClick={cancelEdit} className="px-4 py-2 rounded-full bg-white border border-[#EFE6CF]">Cancel</button>
            </div>
          </section>
        )}

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ambassador-list">
          {list === null && <p className="text-sm text-[#0A1F2E]/55">Loading…</p>}
          {list && list.length === 0 && (
            <p className="text-sm text-[#0A1F2E]/55 col-span-full">No ambassadors yet. Click <strong>Seed 10</strong> to bootstrap the 10 curated profiles, or <strong>New</strong> to add one manually.</p>
          )}
          {list && list.map((a) => (
            <article
              key={a.id}
              data-testid={`ambassador-card-${a.id}`}
              className={`bg-white border rounded-2xl p-5 ${a.visible ? 'border-[#EFE6CF]' : 'border-[#EFE6CF] opacity-60'}`}
            >
              <div className="flex items-start gap-4">
                <img
                  src={a.profile_photo || `https://api.dicebear.com/8.x/lorelei/svg?seed=${a.first_name}${a.last_name}`}
                  alt={`${a.first_name} ${a.last_name}`}
                  className="w-14 h-14 rounded-full bg-[#F4ECD8]"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[#0A1F2E]">{a.first_name} {a.last_name}</p>
                    <span
                      title="Visible only in admin"
                      data-testid={`ambassador-badge-${a.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#2CA8A0]/12 text-[#1A5C6E] font-mono uppercase text-[9px] tracking-[0.18em]"
                    >
                      <BadgeCheck className="w-3 h-3" /> Mundalo Community
                    </span>
                  </div>
                  <p className="text-xs text-[#0A1F2E]/55 break-all">{a.email}</p>
                  <p className="text-xs text-[#0A1F2E]/55">
                    {a.destination_city} · {a.home_state} · {a.age || '-'}
                  </p>
                  <p className="text-sm text-[#0A1F2E]/85 mt-2 line-clamp-3">{a.bio}</p>
                </div>
              </div>

              {/* iter 160 - AI posting controls */}
              <div className="mt-4 pt-4 border-t border-[#F4ECD8]" data-testid={`ambassador-ai-controls-${a.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#1A5C6E] flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    AI posting
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-[11px] text-[#0A1F2E]/70">
                      {a.ai_posting_enabled ? 'On' : 'Off'}
                    </span>
                    <input
                      type="checkbox"
                      checked={!!a.ai_posting_enabled}
                      onChange={() => toggleAiPosting(a)}
                      data-testid={`ambassador-ai-toggle-${a.id}`}
                      className="accent-[#2CA8A0] w-4 h-4"
                    />
                  </label>
                </div>

                <div className="mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-[#5A6B73] mb-1">
                    Topic focus ({(a.topic_focus || []).length}/4)
                  </p>
                  <div className="flex flex-wrap gap-1" data-testid={`ambassador-topics-${a.id}`}>
                    {TOPIC_VOCAB.map((t) => {
                      const active = (a.topic_focus || []).includes(t);
                      const disabled = !active && (a.topic_focus || []).length >= 4;
                      return (
                        <button
                          key={t}
                          onClick={() => toggleTopic(a, t)}
                          disabled={disabled}
                          data-testid={`ambassador-${a.id}-topic-${t}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                            active
                              ? 'bg-[#2CA8A0] border-[#2CA8A0] text-white'
                              : 'bg-white border-[#EFE6CF] text-[#0A1F2E]/70 hover:bg-[#F4ECD8]'
                          } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-[#5A6B73] uppercase tracking-wider mr-1">Every</span>
                    {FREQ_OPTIONS.map((days) => (
                      <button
                        key={days}
                        onClick={() => setFrequency(a, days)}
                        data-testid={`ambassador-${a.id}-freq-${days}`}
                        className={`text-[10px] px-2 py-0.5 rounded border transition ${
                          a.posting_frequency_days === days
                            ? 'bg-[#0A1F2E] border-[#0A1F2E] text-white'
                            : 'bg-white border-[#EFE6CF] text-[#0A1F2E]/70 hover:bg-[#F4ECD8]'
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                  {/* iter 162 - Post-now with countdown + confirmation */}
                  {(() => {
                    const last = postNowCooldowns[a.id];
                    const remaining = last ? Math.max(0, 60 - Math.floor((Date.now() - last) / 1000)) : 0;
                    const cooling = remaining > 0;
                    return (
                      <button
                        onClick={() => requestPostNow(a)}
                        disabled={cooling}
                        data-testid={`ambassador-post-now-${a.id}`}
                        title={cooling
                          ? `Cooling down - try again in ${remaining}s`
                          : "Generate + publish a post right now"}
                        className={`text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-full flex items-center gap-1 transition ${
                          cooling
                            ? 'bg-[#F4ECD8] text-[#0A1F2E]/40 cursor-not-allowed'
                            : 'bg-[#E8A830] text-[#0A1F2E] hover:brightness-95'
                        }`}
                      >
                        <Send className="w-3 h-3" />
                        {cooling ? `Wait ${remaining}s` : 'Post now'}
                      </button>
                    );
                  })()}
                </div>

                {/* iter 162 - Inline result flash (success/error) per ambassador */}
                {postNowFlash[a.id] && (
                  <div
                    data-testid={`ambassador-post-now-flash-${a.id}`}
                    className={`mt-2 text-[11px] px-2 py-1.5 rounded border ${
                      postNowFlash[a.id] === 'success'
                        ? 'bg-[#2CA8A0]/10 border-[#2CA8A0]/30 text-[#1A5C6E]'
                        : postNowFlash[a.id] === 'pending'
                        ? 'bg-[#F4ECD8] border-[#EFE6CF] text-[#0A1F2E]/70'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {postNowFlashMsg[a.id]}
                  </div>
                )}

                {a.next_post_date && (
                  <p className="text-[10px] text-[#5A6B73] mt-2">
                    Next scheduled: {new Date(a.next_post_date).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-1 mt-4">
                <button
                  onClick={() => toggleVisible(a)}
                  title={a.visible ? 'Hide' : 'Show'}
                  data-testid={`ambassador-toggle-${a.id}`}
                  className="p-2 rounded hover:bg-[#F9F6EF]"
                >
                  {a.visible ? <Eye className="w-4 h-4 text-[#1A5C6E]" /> : <EyeOff className="w-4 h-4 text-[#0A1F2E]/40" />}
                </button>
                <button
                  onClick={() => startEdit(a)}
                  data-testid={`ambassador-edit-${a.id}`}
                  className="px-3 py-1.5 text-xs rounded-full bg-[#F4ECD8] text-[#0A1F2E] hover:brightness-95"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(a)}
                  title="Delete"
                  data-testid={`ambassador-delete-${a.id}`}
                  className="p-2 rounded hover:bg-red-50 text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </section>
      </div>

      {/* iter 162 - Post-now confirmation modal. Required before any AI
          post is generated, replaces the prior window.confirm() so we
          have a richer warning + can't be dismissed by Enter-spam. */}
      {confirmPostNow && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="ambassador-post-now-confirm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-[#EFE6CF]">
            <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-2">
              Generate a post as {confirmPostNow.first_name}?
            </h3>
            <p className="text-sm text-[#0A1F2E]/75 leading-relaxed mb-4">
              This calls Claude Sonnet and publishes one new post to the{' '}
              <span className="font-medium">{confirmPostNow.destination_city || 'community'}</span>{' '}
              board immediately. The button will then disable for 60 seconds.
            </p>
            <div className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-lg p-3 mb-5">
              <p className="text-[11px] uppercase tracking-wider font-mono text-[#1A5C6E] mb-1">
                Hard limit
              </p>
              <p className="text-sm text-[#0A1F2E]/85">
                Maximum 1 post per ambassador per 24 hours. If {confirmPostNow.first_name} has
                already posted in the last day, this will be rejected by the server.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelPostNow}
                data-testid="ambassador-post-now-cancel-btn"
                className="px-4 py-2 text-sm rounded-full bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#F9F6EF]"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndPostNow}
                data-testid="ambassador-post-now-confirm-btn"
                className="px-4 py-2 text-sm rounded-full bg-[#E8A830] text-[#0A1F2E] hover:brightness-95 font-medium"
                autoFocus
              >
                Post now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
